import { useParams } from "react-router-dom";

import {
  documentFactOf,
  legacyRowsOf,
  tierLimitOf,
  toolCapsOf,
  tiersOf,
  type McpCatalog,
  type McpQuotaTier,
  type McpTiers,
} from "../../api/mcp";
import { TierRecentChanges } from "../../components/audit/TierRecentChanges";
import { Breadcrumbs } from "../../components/Layout";
import { ResourceView } from "../../components/states";
import { CapEditor } from "../../components/mcp/CapEditor";
import { RetiredCaps } from "../../components/mcp/RetiredCaps";
import { TierDefaults } from "../../components/mcp/TierDefaults";
import { TierRateLimits } from "../../components/mcp/TierRateLimits";
import { RemoveOverride } from "../../components/mcp/RemoveOverride";
import { Notice, Page } from "../../components/ui";
import type { AppConfig } from "../../config";
import { useResource } from "../../hooks/useResource";

/**
 * One tier's caps, with every tool listed — not just the ones with rows.
 *
 * A page showing ten rows out of ninety reads as "the rest are unmanaged" when
 * the truth is "the rest inherit the tier's tool_default". Listing all of them,
 * with where each value comes from, is the difference.
 *
 * Reads `GET /admin/quota/{tier}`: `config` is the stored document, `effective`
 * what the server applies. That route does not 404 for a tier with no
 * document: it answers `source: "none"` with the code defaults. A tier is
 * unknown when that is the answer AND `GET /admin/tiers` does not list it
 * (DA-3678): then the page says "No such tier" and offers no editor, whose
 * first save would CREATE the tier. `free` is listed with no document on a
 * fresh environment and is real, so it keeps the editor. If the list cannot be
 * read, the page does not claim the tier is missing.
 */
export function CapsTier({ config }: { config: AppConfig }) {
  const { tier = "" } = useParams();
  const quota = useResource<McpQuotaTier>(
    config,
    `/mcp/admin/quota/${encodeURIComponent(tier)}`,
    [tier],
  );
  const catalog = useResource<McpCatalog>(config, "/mcp/admin/catalog");
  const known = useResource<McpTiers>(config, "/mcp/admin/tiers");
  const s = quota.state;
  const unknown =
    s.kind === "ready" &&
    s.data?.source === "none" &&
    known.state.kind === "ready" &&
    !tiersOf(known.state.data).includes(tier);

  return (
    <Page title={<span className="ref">{tier}</span>} subtitle="Daily credit caps for this tier.">
      <Breadcrumbs
        trail={[
          { label: "Overview", to: "/" },
          { label: "Tiers & limits", to: "/policy/tiers" },
          { label: tier },
        ]}
      />

      <ResourceView state={s} what="this tier's caps" onRetry={quota.reload} />

      {unknown && (
        <div className="mt-4">
          <Notice title="No such tier">
            <p>
              MCP has no tier called <span className="ref">{tier}</span>. Check the name on{" "}
              <a className="underline" href="/policy/tiers">
                Tiers &amp; limits
              </a>
              , where a new tier is created on purpose.
            </p>
          </Notice>
        </div>
      )}

      {s.kind === "ready" &&
        !unknown &&
        (() => {
          const caps = toolCapsOf(s.data.config);
          const toolDefault = tierLimitOf(s.data, "tool_default");
          const cat = catalog.state.kind === "ready" ? catalog.state.data : null;

          const rows = legacyRowsOf(s.data);

          return (
            <>
              <p className="mt-4 text-sm text-slate-600 dark:text-slate-300">{documentFactOf(s.data)}</p>
              {rows.length > 0 && (
                <div className="mt-2 text-sm">
                  <p>Old rows still in the table.</p>
                  <ul className="mt-1">
                    {rows.map((name) => (
                      <li key={name}>
                        <span className="ref">{name}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-4">
                <TierDefaults config={config} tier={tier} quota={s.data} onDone={quota.reload} />
              </div>

              <div className="mt-4">
                <RetiredCaps
                  config={config}
                  tier={tier}
                  caps={caps}
                  catalog={cat}
                  effectiveWithinSeconds={s.data.effective_within_seconds}
                  onDone={quota.reload}
                />
              </div>

              <div className="mt-4">
                <CapEditor
                  config={config}
                  tier={tier}
                  caps={caps}
                  toolDefault={toolDefault}
                  catalog={cat}
                  onDone={quota.reload}
                />
              </div>

              {/* The other half of editing a cap: taking one away. Without it
                  an override added by mistake could only be overwritten with
                  another number, never removed. */}
              <div className="mt-6">
                <RemoveOverride
                  config={config}
                  tier={tier}
                  caps={caps}
                  toolDefault={toolDefault}
                  onDone={quota.reload}
                />
              </div>

              {/* RATE LIMITS LIVE HERE NOW. They were two lane cards in
                  global Settings writing `PATCH /admin/ratelimit/{lane}`,
                  which MCP retired — they are part of the tier document and
                  are set with the caps above, on the same route. A lane set
                  globally was always a fiction: the runtime applies it per
                  tier. */}
              <div className="mt-6">
                <TierRateLimits
                  config={config}
                  tier={tier}
                  quota={s.data}
                  onDone={quota.reload}
                />
              </div>
            </>
          );
        })()}
      {/* Removals included (DA-3706), and in words (DA-3705). The tier's
          default today is what the newest removal fell to. */}
      <div className="mt-4">
        <TierRecentChanges
          config={config}
          tier={tier}
          toolDefault={s.kind === "ready" ? tierLimitOf(s.data, "tool_default") : undefined}
        />
      </div>

    </Page>
  );
}
